import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Clock, Package, AlertCircle, UserPlus } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderCompletePage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const reference = searchParams.get('ref');
  const { token, user, register } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  
  // Guest signup state
  const [showSignup, setShowSignup] = useState(false);
  const [signupData, setSignupData] = useState({ password: '', confirmPassword: '' });
  const [signingUp, setSigningUp] = useState(false);

  useEffect(() => {
    fetchOrderStatus();
    fetchRecommendedProducts();
  }, [orderId, reference]);

  const fetchRecommendedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=4`);
      setRecommendedProducts(response.data.slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch recommended products:', error);
    }
  };

  const handleGuestSignup = async (e) => {
    e.preventDefault();
    if (signupData.password !== signupData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (signupData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSigningUp(true);
    try {
      const guestEmail = order?.guest_email;
      const guestName = order?.shipping_address?.full_name || 'Customer';
      
      await register(guestEmail, guestName, signupData.password);
      toast.success('Account created! You can now track all your orders.');
      setShowSignup(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setSigningUp(false);
    }
  };

  const fetchOrderStatus = async () => {
    try {
      const gateway = searchParams.get('gateway');
      if (orderId && ['squad', 'bachs'].includes(gateway) && status !== 'cancelled') {
        await axios.post(`${API}/payments/${gateway}/confirm/${orderId}`).catch(() => {});
      }
      // Try to get order by ID first
      if (orderId) {
        const response = await axios.get(`${API}/paysafe/status/${orderId}`);
        setOrder(response.data);
      } else if (reference) {
        // Fallback to reference lookup
        const response = await axios.get(`${API}/paysafe/verify?ref=${reference}`);
        setOrder(response.data);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = () => {
    const paymentStatus = order?.payment_status || status;
    
    // Payment cancelled
    if (paymentStatus === 'cancelled' || status === 'cancelled') {
      return {
        icon: <AlertCircle className="h-12 w-12 text-red-600" />,
        bgColor: 'bg-red-100',
        title: 'Payment Cancelled',
        message: 'Your payment was cancelled. Your order has not been processed. You can try again by placing a new order.',
        color: 'text-red-600'
      };
    }
    
    // Payment failed
    if (paymentStatus === 'failed' || status === 'failed') {
      return {
        icon: <AlertCircle className="h-12 w-12 text-red-600" />,
        bgColor: 'bg-red-100',
        title: 'Payment Failed',
        message: 'Your payment could not be processed. Please try again or use a different payment method.',
        color: 'text-red-600'
      };
    }
    
    // Payment fully approved and paid
    if (paymentStatus === 'paid' || paymentStatus === 'approved') {
      return {
        icon: <CheckCircle className="h-12 w-12 text-green-600" />,
        bgColor: 'bg-green-100',
        title: 'Payment Approved!',
        message: 'Your payment has been verified. Your order is now being processed for shipping.',
        color: 'text-green-600'
      };
    }
    
    // Paysafe pending approval - This is the key state for Paysafe returns
    if (paymentStatus === 'pending_approval' || paymentStatus === 'pending_review') {
      return {
        icon: <Clock className="h-12 w-12 text-amber-600" />,
        bgColor: 'bg-amber-100',
        title: 'Payment Waiting Approval',
        message: 'Your payment has been submitted and is awaiting admin verification. You will receive an email confirmation once your payment is approved.',
        color: 'text-amber-600'
      };
    }
    
    // Returned from Paysafe gateway with status=paid means submitted, NOT approved
    if (status === 'paid' && paymentStatus !== 'paid') {
      return {
        icon: <Clock className="h-12 w-12 text-amber-600" />,
        bgColor: 'bg-amber-100',
        title: 'Payment Waiting Approval',
        message: 'Your payment has been submitted successfully. Our team will verify it within 24 hours and you will receive an email confirmation.',
        color: 'text-amber-600'
      };
    }
    
    if (paymentStatus === 'rejected') {
      return {
        icon: <AlertCircle className="h-12 w-12 text-red-600" />,
        bgColor: 'bg-red-100',
        title: 'Payment Rejected',
        message: 'There was an issue with your payment verification. Please contact support or try again.',
        color: 'text-red-600'
      };
    }
    
    // Pending payment (order created but payment not yet processed)
    if (paymentStatus === 'pending' || paymentStatus === 'pending_payment') {
      return {
        icon: <Clock className="h-12 w-12 text-amber-600" />,
        bgColor: 'bg-amber-100',
        title: 'Awaiting Payment',
        message: 'Your order has been created. Please complete the payment to proceed.',
        color: 'text-amber-600'
      };
    }
    
    return {
      icon: <Clock className="h-12 w-12 text-gray-600" />,
      bgColor: 'bg-gray-100',
      title: 'Order Received',
      message: 'Your order has been received. Please complete payment to proceed.',
      color: 'text-gray-600'
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-900">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-black mx-auto mb-6"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay();

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center" data-testid="order-complete-page">
          {/* Status Icon */}
          <div className={`${statusDisplay.bgColor} w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6`}>
            {statusDisplay.icon}
          </div>

          {/* Title */}
          <h1 className="font-heading text-4xl font-bold mb-4" data-testid="order-status-title">
            {statusDisplay.title}
          </h1>

          {/* Message */}
          <p className="text-xl text-gray-600 mb-8 max-w-lg mx-auto">
            {statusDisplay.message}
          </p>

          {/* Order Details Box */}
          <div className="bg-gray-50 p-8 mb-8 text-left max-w-md mx-auto rounded-lg">
            <h2 className="font-heading text-lg font-bold mb-4 flex items-center">
              <Package className="mr-2 h-5 w-5" />
              Order Details
            </h2>
            <div className="space-y-3 text-sm">
              {orderId && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Order ID:</span>
                  <span className="font-mono font-medium">{orderId.substring(0, 8)}...</span>
                </div>
              )}
              {reference && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Reference:</span>
                  <span className="font-mono font-medium">{reference}</span>
                </div>
              )}
              {order?.total_amount && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-bold">${order.total_amount}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Payment Status:</span>
                <span className={`font-bold ${statusDisplay.color}`} data-testid="payment-status-value">
                  {order?.payment_status === 'pending_approval' || order?.payment_status === 'pending_review' 
                    ? 'Waiting Approval' 
                    : order?.payment_status === 'paid' 
                    ? 'Approved' 
                    : order?.payment_status === 'cancelled'
                    ? 'Cancelled'
                    : order?.payment_status === 'failed'
                    ? 'Failed'
                    : order?.payment_status === 'pending'
                    ? 'Pending'
                    : order?.payment_status || 'Submitted'}
                </span>
              </div>
            </div>
          </div>

          {/* Failed/Cancelled Payment Info */}
          {(order?.payment_status === 'cancelled' || order?.payment_status === 'failed' || status === 'cancelled' || status === 'failed') && (
            <div className="bg-red-50 border border-red-200 p-6 mb-8 max-w-md mx-auto rounded-lg" data-testid="payment-failed-info">
              <h3 className="font-bold text-red-800 mb-2">Payment Not Completed</h3>
              <p className="text-sm text-red-700 mb-4">
                {order?.payment_status === 'cancelled' || status === 'cancelled' 
                  ? 'You cancelled the payment process. Your order was not processed.'
                  : 'There was an error processing your payment. This could be due to insufficient funds, expired card, or a network issue.'}
              </p>
              <Link to="/products">
                <Button className="w-full bg-red-600 text-white hover:bg-red-700">
                  Try Again - Shop Now
                </Button>
              </Link>
            </div>
          )}

          {/* Pending Approval Info */}
          {(order?.payment_status === 'pending_approval' || order?.payment_status === 'pending_review' || (status === 'paid' && order?.payment_status !== 'paid')) && (
            <div className="bg-amber-50 border border-amber-200 p-6 mb-8 max-w-md mx-auto rounded-lg" data-testid="pending-approval-info">
              <h3 className="font-bold text-amber-800 mb-2">What happens next?</h3>
              <ol className="text-sm text-amber-700 text-left space-y-2">
                <li>1. Your payment has been submitted to our team</li>
                <li>2. Admin will review and verify your payment</li>
                <li>3. You'll receive an email confirmation within 24 hours</li>
                <li>4. Once approved, your order ships immediately</li>
              </ol>
              <p className="text-xs text-amber-600 mt-4 italic">
                Note: Your order will remain pending until admin approval is complete.
              </p>
            </div>
          )}

          {/* Guest Signup Prompt - Only for guest orders and not logged in */}
          {order?.guest_email && !user && (
            <div className="bg-blue-50 border border-blue-200 p-6 mb-8 max-w-md mx-auto rounded-lg" data-testid="guest-signup-prompt">
              {!showSignup ? (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <UserPlus className="h-6 w-6 text-blue-600" />
                    <h3 className="font-bold text-blue-800">Track Your Order Anytime</h3>
                  </div>
                  <p className="text-sm text-blue-700 mb-4">
                    Create an account to easily track this order and all future orders. 
                    We'll use your email <strong>{order.guest_email}</strong> to set up your account.
                  </p>
                  <Button 
                    onClick={() => setShowSignup(true)}
                    className="w-full bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Create Account
                  </Button>
                </>
              ) : (
                <form onSubmit={handleGuestSignup}>
                  <h3 className="font-bold text-blue-800 mb-3">Create Your Account</h3>
                  <p className="text-xs text-blue-600 mb-4">Email: {order.guest_email}</p>
                  <div className="space-y-3">
                    <Input
                      type="password"
                      placeholder="Create a password"
                      value={signupData.password}
                      onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                      required
                      minLength={6}
                    />
                    <Input
                      type="password"
                      placeholder="Confirm password"
                      value={signupData.confirmPassword}
                      onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                      required
                    />
                    <div className="flex gap-2">
                      <Button 
                        type="submit"
                        disabled={signingUp}
                        className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                      >
                        {signingUp ? 'Creating...' : 'Create Account'}
                      </Button>
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={() => setShowSignup(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dashboard">
              <Button 
                className="bg-black text-white hover:bg-zinc-800 py-4 px-8 uppercase tracking-widest text-xs font-bold"
                data-testid="view-orders-button"
              >
                View My Orders
              </Button>
            </Link>
            <Link to="/products">
              <Button 
                variant="outline" 
                className="border border-gray-200 hover:border-black py-4 px-8 uppercase tracking-widest text-xs font-bold"
              >
                Continue Shopping
              </Button>
            </Link>
          </div>

          {/* Recommended Products Section */}
          {recommendedProducts.length > 0 && (
            <div className="mt-16 border-t pt-12 max-w-4xl mx-auto" data-testid="order-complete-recommendations">
              <h2 className="font-heading text-xl font-bold mb-6 text-center">You May Also Like</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {recommendedProducts.map((product) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.id}`}
                    className="group"
                  >
                    <div className="aspect-square bg-gray-100 mb-3 overflow-hidden">
                      <img
                        src={product.images?.[0] || product.image_url || '/placeholder.png'}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                    <h3 className="font-medium text-sm truncate group-hover:text-gray-600">
                      {product.name}
                    </h3>
                    <p className="text-sm font-bold mt-1">${product.price?.toFixed(2)}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderCompletePage;
